import { Injectable, inject } from '@angular/core';
import { calculationConverter } from '@seu-converter';
import { RequestService, StoreService, ToastService } from '@seu-services';
import {
  SEU_API_ENDPOINTS,
  SeuActiveCalculation,
  SeuCalculationPayload,
  SeuCalculationResponseApi,
} from '@seu-types';
import { first } from 'lodash-es';
import {
  Subject,
  catchError,
  firstValueFrom,
  map,
  of,
  race,
  take,
  timer,
} from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class CalculationService {
  private readonly requestService = inject(RequestService);
  private readonly store = inject(StoreService);
  private readonly toastService = inject(ToastService);

  private readonly cancelPolling$$ = new Subject<void>();

  public cancelAllPolling(): void {
    this.cancelPolling$$.next();
  }

  public async doCalculation(
    offerId: string,
    activeCalculation: SeuActiveCalculation
  ): Promise<boolean> {
    const endpoint = SEU_API_ENDPOINTS.calculation.replace(':offerId', offerId);

    const data: SeuCalculationPayload = {
      computationInput: {
        globalParameterSetId: activeCalculation.globalParameterSetId,
        interconnectionId: activeCalculation.interconnectionId,
        operatingConditionId: activeCalculation.operatingConditionId,
      },
    };

    while (true) {
      const responseData = await firstValueFrom(
        this.requestService
          .post<SeuCalculationResponseApi>(endpoint as SEU_API_ENDPOINTS, data)
          .pipe(
            catchError(() => {
              this.toastService.addToast({
                type: 'error',
                text: 'calculation.toasts.calculation-error',
              });

              return of(null);
            })
          )
      );

      if (!responseData) {
        return false;
      }

      const calculationResponseItems =
        calculationConverter.fromApi(responseData);

      const calculationResponseItem = first(calculationResponseItems);

      if (!calculationResponseItem) {
        console.error('Calculation response is not valid');
        return false;
      }

      if (calculationResponseItem.pollingIntervalSeconds === 0) {
        this.store.addCalculationResponseItem({
          ...calculationResponseItem,
          uid: activeCalculation.uid,
        });
        return true;
      }

      const outcome = await firstValueFrom(
        race(
          timer(calculationResponseItem.pollingIntervalSeconds * 1000).pipe(
            map(() => 'continue' as const)
          ),
          this.cancelPolling$$.pipe(
            take(1),
            map(() => 'cancelled' as const)
          )
        )
      );

      if (outcome === 'cancelled') {
        return false;
      }
    }
  }
}
